import { css } from "../helpers/webComponents";

/**
 * /** CSS affecting the {@link SoundComponents} */
const styles: string = css`
    :host {
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        display: none;
        justify-content: center;
        align-items: center;
        background: rgba(0, 0, 0, 0.7);
        z-index: 100;
        
    }

    :host(.active) {
        display: flex;
    }

    .settings-menu {
        background-color: #332c57;
        padding: 20px;
        border-radius: 10px;
        box-shadow: 0 0 10px rgba(0, 0, 0, 0.3);
        color: white;
        text-align: center;
        min-width: 300px;
    }

    label {
        display: block;
        margin-bottom: 10px;
    }

    input[type="range"] {
        width: 100%;
    }

    input[type="checkbox"] {
        display: block;
        margin: 10px auto; 
    }

    .close-button {
        background: #7f6ed7;
        color: white;
        border: none;
        padding: 5px 10px;
        cursor: pointer;
        margin-top: 10px;
        font-family: inherit; 
        font-size: inherit;
    }
  
    .close-button:hover {
        background: #52478b;
    }
`;

/**
 * A web component for managing sound settings.
 * Allows users to control volume and toggle background music.
 *
 * @extends HTMLElement
 */
export class SoundComponents extends HTMLElement {
    private audioElement: HTMLAudioElement;
    private _isMusicEnabled: boolean;
    private _volume: number;

    /**
     * Constructs a new `SoundComponents` instance.
     * Initializes the audio element, default values, and event listeners.
     */
    public constructor() {
        super();
        this.attachShadow({ mode: "open" });

        this.audioElement = new Audio();
        this.audioElement.loop = true;
        this.audioElement.volume = 1;

        this._isMusicEnabled = true;
        this._volume = 1;

        this.render();
        this.listenForKeyPress();
    }

    /**
     * Renders the settings menu inside the shadow DOM.
     * Generates UI elements for volume control and music toggle.
     */
    private render(): void {
        if (!this.shadowRoot) return;

        this.shadowRoot.innerHTML = `
            <style>${styles}</style>
            <div class="settings-menu">
                <label for="volume">Volume</label>
                <input type="range" id="volume" min="0" max="1" step="0.01" value="${this._volume}">
                <span id="volume-label">${(this._volume * 100).toFixed(0)}%</span>
                <label for="toggle-music">Enable Music</label>
                <input type="checkbox" id="toggle-music" ${this._isMusicEnabled ? "checked" : ""}>
                <button class="close-button" id="close-settings">Close</button>
            </div>
        `;

        this.setupEventListeners();
    }

    /**
     * Sets up event listeners for user interactions, such as volume adjustment and toggling music.
     */
    private setupEventListeners(): void {
        if (!this.shadowRoot) return;

        const volumeControl: HTMLInputElement | null = this.shadowRoot.querySelector<HTMLInputElement>("#volume");
        const volumeLabel: HTMLElement | null = this.shadowRoot.querySelector<HTMLElement>("#volume-label");
        const toggleMusic: HTMLInputElement | null = this.shadowRoot.querySelector<HTMLInputElement>("#toggle-music");
        const closeButton: HTMLButtonElement | null = this.shadowRoot.querySelector<HTMLButtonElement>("#close-settings");

        if (!volumeControl || !volumeLabel || !toggleMusic || !closeButton) {
            console.error("One or more elements not found in the shadow DOM");
            return;
        }

        volumeControl.addEventListener("input", event => {
            const target: HTMLInputElement = event.target as HTMLInputElement;
            this.volume = parseFloat(target.value);
            volumeLabel.textContent = `${(this.volume * 100).toFixed(0)}%`;
        });

        toggleMusic.addEventListener("change", event => {
            const target: HTMLInputElement = event.target as HTMLInputElement;
            this.isMusicEnabled = target.checked;
        });

        closeButton.addEventListener("click", () => {
            this.classList.remove("active");
        });
    }

    /**
     * Listens for the "Escape" key to toggle the settings menu visibility.
     */
    private listenForKeyPress(): void {
        window.addEventListener("keydown", (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                this.classList.toggle("active");
            }
        });
    }

    /**
     * Gets whether music is currently enabled.
     *
     * @returns {boolean} `true` if music is enabled, otherwise `false`.
     */
    public get isMusicEnabled(): boolean {
        return this._isMusicEnabled;
    }

    /**
     * Enables or disables background music.
     * If enabled, plays the current audio.
     *
     * @param {boolean} enabled - Whether to enable or disable music.
     */
    public set isMusicEnabled(enabled: boolean) {
        this._isMusicEnabled = enabled;

        if (enabled) {
            if (this.audioElement.src && this.audioElement.src.trim() !== "") {
                // Only play if a valid audio source is set
                this.audioElement.volume = this._volume;
                this.audioElement.play().catch((error: unknown) => console.error(error));
            }
            else {
                console.warn("No valid audio source to play when unmuting.");
            }
        }
        else {
            this.audioElement.pause();
        }
    }

    /**
     * Gets the current volume level.
     *
     * @returns {number} The volume level (between 0 and 1).
     */
    public get volume(): number {
        return this._volume;
    }

    /**
     * Sets the volume level and updates the audio element.
     *
     * @param {number} value - The volume level (between 0 and 1).
     */
    public set volume(value: number) {
        this._volume = value;
        this.audioElement.volume = value;
    }

    /**
     * Plays the background music with an optional specified file.
     *
     * @param {string} [musicFile] - The file path of the music to play. If not provided, plays the current file.
     */
    public playBackgroundMusic(musicFile?: string): void {
        if (musicFile) {
            this.audioElement.src = `/assets/sounds/${musicFile}`;
        }

        if (this._isMusicEnabled) {
            this.audioElement.play().catch((error: unknown) => console.error(error));
        }
    }

    /**
     * Stops the currently playing background music and resets playback position.
     */
    public stopBackgroundMusic(): void {
        this.audioElement.pause();
        this.audioElement.currentTime = 0;
        this.audioElement.src = ""; // Clear the audio source to prevent resuming old music
    }
}

// Define the custom web component.
customElements.define("sound-component", SoundComponents);
